/**
 * A job’s board, and the whole book of work across jobs.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const PIPELINE_ROUTES: readonly RouteManifestEntry[] = [
  // The board hangs off a job but is gated on `pipeline`, not on `clients`. The two are separate
  // purchases: an agency can buy client and requisition management without buying the execution
  // module, and the screen that would break in that case is this one — so it is the one that
  // carries the key. Everyone in the tenant may look; `write application` decides who may move a
  // card, and the screen renders the controls accordingly.
  {
    id: 'job-pipeline',
    path: '/jobs/$jobId/pipeline',
    entitlement: 'pipeline',
    authenticated: true,
    /*
     * Which view opens, and where the reader came from — CO10 §4.
     *
     * **`view`**: the board's views were `useState` and therefore unlinkable, which was fine while
     * the only way in was the control itself. My Pipeline links *into* the stuck view, because a
     * row that has just said "4 waiting on us" and then lands the reader on the default board has
     * made them find it a second time.
     *
     * **`from`**: a board reached from My Pipeline must go back to My Pipeline. Carried in the URL
     * rather than read from history or `document.referrer`, for two reasons — it survives a reload
     * and a shared link, and it is *declared* rather than sniffed, so the back link cannot start
     * lying the day some other screen begins linking here. Absent means the ordinary route in, and
     * the ordinary answer: back to the job.
     *
     * Both are plain strings narrowed by the screen. An unknown value opens the board and points
     * back at the job, which is the right failure for something somebody typed into a URL bar.
     */
    validateSearch: (search) => ({
      ...(typeof search.view === 'string' ? { view: search.view } : {}),
      ...(typeof search.from === 'string' ? { from: search.from } : {}),
    }),
    load: () => import('../screens/job-pipeline.js'),
  },
  /*
   * The rest of §7.2's sections that belong to this module rather than to `clients`.
   *
   * They are tabs on the job record and they live here, in the pipeline manifest, because that is
   * where their entitlement is true: an agency that has not bought the execution module has no
   * applications, no rounds and no submissions, and must see no tab for them. `job-pipeline` above
   * already had this shape and is the reason the record's tabs are routes at all.
   */
  {
    id: 'job-interviews',
    path: '/jobs/$jobId/interviews',
    entitlement: 'pipeline',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
  {
    id: 'job-submissions',
    path: '/jobs/$jobId/submissions',
    entitlement: 'pipeline',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
  {
    id: 'job-plan',
    path: '/jobs/$jobId/plan',
    entitlement: 'pipeline',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
  {
    // The requisition's own documents — 01 Oct 2026. `clients`, not `pipeline`: the job description
    // is the client's and exists before any pipeline does.
    id: 'job-documents',
    path: '/jobs/$jobId/documents',
    entitlement: 'clients',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
  /*
   * My Pipeline — CO10, M-34. Two views over one desk on one nav item.
   *
   * §6.5's book of work is still here; it is the *Candidatures* view rather than the screen, and
   * the default is the requisition worklist CO10 asks for. One item rather than two, because two
   * destinations both called some form of "my pipeline" is the second-source-of-truth problem
   * conventions §5 forbids — CO10 §8.1 is where that was decided.
   *
   * Gated on `pipeline` for the reason the board is: it reads applications, and an agency that has
   * not bought the execution module has none. It is a nav item rather than a tab on Jobs because
   * the question it answers — "which of mine needs me first" — is not a question about any one
   * requisition.
   *
   * ── The nav reads **Funnel** — 30 Aug 2026, asked for directly ────────────────────────────────
   *
   * *"Rename 'My Pipeline' to 'Funnel' — only on the UI."* So the label moved and nothing else did:
   * the route is still `/pipeline`, the id still `my-pipeline`, the catalogue namespace still
   * `mine.*`. That is the same line M-11 drew when Standup became Huddle — the label is the only
   * name a user reads, and every other one is a name only this repository reads, whose renaming
   * would break the tracker's and the attack plan's references for nobody's benefit.
   *
   * The word is also better than the one it replaces. *My pipeline* names a possessive that the
   * scope selector above it already controls — a manager on agency scope was reading "my" over a
   * list that is not theirs — while a funnel names the shape of the thing: requisitions narrowing
   * toward a hire. And this nav item sits beside a *Pipeline board* on every job, which is a
   * different surface; two items sharing a word is how somebody clicks the wrong one twice.
   */
  {
    id: 'my-pipeline',
    path: '/pipeline',
    entitlement: 'pipeline',
    // Read by everybody in the tenant; the server narrows what comes back to the scope they hold,
    // and `write application` decides whether the bulk controls render at all.
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    nav: { labelKey: 'mine.nav', section: 'recruiting', order: 4 },
    load: () => import('../screens/my-pipeline.js'),
  },
  /*
   * The offers list — M-22, CO7 §5.4.
   *
   * A nav **item**, not a new section, which is what CO6 §5 said the three-section shape was for:
   * "later packs need no new section", and an offer is recruiting work.
   *
   * `requires` names `offer` rather than `application`, and that is the whole access decision. An
   * offer carries a person's salary and `read application` is held by everybody who works a
   * pipeline — including `read_only`, `sourcer`, `coordinator` and `bd`, none of whom hold any
   * grant on `offer` at all. So this item is simply absent from their sidebar rather than present
   * and refused, which is §16's rule for partial permissions: hide, do not disable-and-tease.
   */
  {
    id: 'offers',
    path: '/offers',
    entitlement: 'pipeline',
    requires: { action: 'read', resource: 'offer' },
    authenticated: true,
    nav: { labelKey: 'offers.nav', section: 'recruiting', order: 6 },
    load: () => import('../screens/offers.js'),
  },
  /*
   * The offer record — the step rail's caller.
   *
   * AFTER `/offers` in this array, deliberately: TanStack matches in order and a `$param` route
   * declared first would swallow the static list. Keyed on the application because all five steps
   * hang off it — see the screen's own header.
   */
  {
    id: 'offer-record',
    path: '/offers/$applicationId',
    entitlement: 'pipeline',
    requires: { action: 'read', resource: 'offer' },
    authenticated: true,
    load: () => import('../screens/offer-record.js'),
  },
];
